import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useVanish, type VanishResult } from '@/hooks/useVanish';
import { useToast } from '@/hooks/useToast';
import { useAppContext } from '@/hooks/useAppContext';
import {
  AlertTriangle,
  Globe,
  Server,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Well-known public relays for global vanish broadcast
const GLOBAL_RELAY_SET = [
  'wss://relay.ditto.pub',
  'wss://relay.primal.net',
  'wss://relay.damus.io',
  'wss://nos.lol',
  'wss://relay.nostr.band',
  'wss://nostr.wine',
  'wss://relay.snort.social',
  'wss://relay.nostr.wf',
  'wss://nostr-pub.wellorder.net',
  'wss://eden.nostr.land',
  'wss://nostr.rocks',
  'wss://nostr.mom',
  'wss://relay.bitcoiner.social',
  'wss://nostr.oxtr.dev',
  'wss://purple.pages.dev',
];

type VanishMode = 'relay' | 'global';
type Step = 'configure' | 'confirm' | 'results';

interface VanishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VanishDialog({ open, onOpenChange }: VanishDialogProps) {
  const { config } = useAppContext();
  const { toast } = useToast();
  const { mutateAsync: vanish, isPending } = useVanish();

  const [mode, setMode] = useState<VanishMode>('relay');
  const [step, setStep] = useState<Step>('configure');
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [selectedRelays, setSelectedRelays] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<VanishResult[]>([]);

  const userRelays = config.relayMetadata.relays.map((r) => r.url);
  const globalRelays = Array.from(new Set([...GLOBAL_RELAY_SET, ...userRelays]));

  const isGlobal = mode === 'global';
  const targetRelays = isGlobal ? globalRelays : Array.from(selectedRelays);
  const CONFIRM_PHRASE = 'VANISH';

  const resetDialog = () => {
    setStep('configure');
    setMode('relay');
    setReason('');
    setConfirmText('');
    setSelectedRelays(new Set());
    setResults([]);
  };

  const handleClose = (open: boolean) => {
    if (!isPending) {
      if (!open) resetDialog();
      onOpenChange(open);
    }
  };

  const toggleRelay = (url: string) => {
    setSelectedRelays((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const canProceedToConfirm = isGlobal ? true : selectedRelays.size > 0;
  const canVanish = confirmText.trim().toUpperCase() === CONFIRM_PHRASE;

  const handleVanish = async () => {
    try {
      const relays = isGlobal ? ['ALL_RELAYS', ...globalRelays] : targetRelays;
      const vanishResults = await vanish({ relays, reason });
      setResults(vanishResults);
      setStep('results');
    } catch (err) {
      toast({
        title: 'Vanish failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const sentCount = results.filter((r) => r.status === 'sent').length;
  const errorCount = results.filter((r) => r.status === 'error').length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-accent/30 max-w-lg">

        {/* ── Step: Configure ── */}
        {step === 'configure' && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded flex items-center justify-center bg-accent/10 border border-accent/30">
                  <img src="/tombstone_skull.png" alt="" className="w-5 h-5 object-contain" />
                </div>
                <div>
                  <DialogTitle className="font-display text-base text-accent">
                    Request to Vanish
                  </DialogTitle>
                  <p className="text-[11px] text-muted-foreground mt-0.5">NIP-62 · kind 62</p>
                </div>
              </div>
              <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
                A <span className="text-foreground font-medium">Request to Vanish</span> asks relays to
                permanently delete <em>all</em> of your events — including DMs — and prevent
                re-broadcasting. This is legally binding in some jurisdictions. It cannot be reversed.
              </DialogDescription>
            </DialogHeader>

            {/* Severity notice */}
            <div className="rounded border border-accent/25 bg-accent/5 p-3 flex gap-2.5">
              <AlertTriangle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
              <div className="space-y-1 text-xs text-muted-foreground">
                <p className="font-medium text-foreground">This is more extreme than NIP-09 deletion.</p>
                <p>NIP-09 (kind 5) targets individual events. NIP-62 (kind 62) requests a relay to
                  delete <strong>everything</strong> from your public key — past, present, and future
                  broadcasts — and block re-entry.</p>
                <p>Relay compliance is voluntary, but supporting relays treat this as permanent.</p>
              </div>
            </div>

            {/* Mode selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Scope</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setMode('relay')}
                  className={cn(
                    'rounded-lg border p-3 text-left transition-all',
                    mode === 'relay'
                      ? 'border-accent/50 bg-accent/5'
                      : 'border-border bg-secondary/30 hover:border-border/70'
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Server className="w-3.5 h-3.5 text-accent" />
                    <span className="text-xs font-medium text-foreground">Selected Relays</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Target specific relays. Sends a relay-tagged kind 62 event directly to each one.
                  </p>
                </button>

                <button
                  onClick={() => setMode('global')}
                  className={cn(
                    'rounded-lg border p-3 text-left transition-all',
                    mode === 'global'
                      ? 'border-accent/50 bg-accent/5'
                      : 'border-border bg-secondary/30 hover:border-border/70'
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Globe className="w-3.5 h-3.5 text-accent" />
                    <span className="text-xs font-medium text-foreground">Global (ALL_RELAYS)</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Broadcasts to all known relays with the <code>ALL_RELAYS</code> sentinel tag.
                  </p>
                </button>
              </div>
            </div>

            {/* Relay selector (per-relay mode) */}
            {mode === 'relay' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                  Target Relays
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    ({selectedRelays.size} selected)
                  </span>
                </Label>
                <div className="max-h-44 overflow-auto space-y-1.5 pr-1">
                  {userRelays.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">
                      No relays configured. Add relays on the Relays page first.
                    </p>
                  ) : (
                    userRelays.map((url) => (
                      <label
                        key={url}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border/50 bg-secondary/30 hover:bg-secondary/50 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={selectedRelays.has(url)}
                          onCheckedChange={() => toggleRelay(url)}
                          className="border-muted-foreground/40 data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                        />
                        <code className="text-xs text-muted-foreground flex-1 truncate font-mono">
                          {url.replace(/^wss?:\/\//, '')}
                        </code>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Global relay count */}
            {mode === 'global' && (
              <div className="rounded-lg border border-border bg-secondary/20 p-3 flex items-center gap-3">
                <Globe className="w-4 h-4 text-accent flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Will broadcast to{' '}
                  <span className="text-foreground font-medium">{globalRelays.length} relays</span>
                  {' '}(your configured relays + well-known public relays).
                </p>
              </div>
            )}

            {/* Reason field */}
            <div className="space-y-2">
              <Label htmlFor="vanish-reason" className="text-sm text-muted-foreground">
                Reason / Legal Notice{' '}
                <span className="text-muted-foreground/60">(optional)</span>
              </Label>
              <Textarea
                id="vanish-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. GDPR right to erasure request, account closure, etc."
                className="bg-secondary/30 border-border resize-none text-sm h-20"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="ghost"
                onClick={() => handleClose(false)}
                className="text-muted-foreground"
              >
                Cancel
              </Button>
              <Button
                onClick={() => setStep('confirm')}
                disabled={!canProceedToConfirm}
                className="tombstone-glow bg-accent text-accent-foreground hover:bg-accent/90"
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── Step: Confirm ── */}
        {step === 'confirm' && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded flex items-center justify-center bg-accent/10 border border-accent/30">
                  <img src="/tombstone_skull.png" alt="" className="w-5 h-5 object-contain" />
                </div>
                <DialogTitle className="font-display text-base text-accent">
                  Final Confirmation
                </DialogTitle>
              </div>
              <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
                You are about to request that{' '}
                {isGlobal ? (
                  <span className="text-foreground font-medium">all supporting relays</span>
                ) : (
                  <span className="text-foreground font-medium">
                    {selectedRelays.size} {selectedRelays.size === 1 ? 'relay' : 'relays'}
                  </span>
                )}{' '}
                permanently delete everything associated with your public key. This cannot be undone.
              </DialogDescription>
            </DialogHeader>

            {/* Summary */}
            <div className="rounded border border-border bg-secondary/30 p-3 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Mode</span>
                <span className="text-foreground font-medium flex items-center gap-1.5">
                  {isGlobal ? (
                    <><Globe className="w-3 h-3" /> Global (ALL_RELAYS)</>
                  ) : (
                    <><Server className="w-3 h-3" /> {selectedRelays.size} selected relays</>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Event kind</span>
                <span className="text-foreground font-mono">62</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Relay count</span>
                <span className="text-foreground font-mono">{targetRelays.length}</span>
              </div>
              {reason && (
                <div className="pt-1 border-t border-border/50">
                  <span className="text-muted-foreground">Reason: </span>
                  <span className="text-foreground italic">{reason.slice(0, 80)}{reason.length > 80 ? '…' : ''}</span>
                </div>
              )}
            </div>

            {/* Confirmation input */}
            <div className="space-y-2">
              <Label htmlFor="vanish-confirm" className="text-sm text-foreground">
                Type <span className="font-mono font-bold text-accent">{CONFIRM_PHRASE}</span> to confirm
              </Label>
              <Input
                id="vanish-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                className="bg-secondary/30 border-border font-mono tracking-widest text-center text-sm uppercase"
                autoComplete="off"
                spellCheck={false}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="ghost"
                onClick={() => setStep('configure')}
                disabled={isPending}
                className="text-muted-foreground"
              >
                Back
              </Button>
              <Button
                onClick={handleVanish}
                disabled={!canVanish || isPending}
                className="tombstone-glow bg-accent text-accent-foreground hover:bg-accent/90 min-w-[140px]"
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    Vanishing…
                  </>
                ) : (
                  <>
                    <img src="/tombstone_skull.png" alt="" className="w-3.5 h-3.5 object-contain mr-2" />
                    Send Vanish Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── Step: Results ── */}
        {step === 'results' && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded flex items-center justify-center border bg-accent/10 border-accent/30">
                  {sentCount > 0
                    ? <CheckCircle2 className="w-5 h-5 text-accent" />
                    : <XCircle className="w-5 h-5 text-muted-foreground" />
                  }
                </div>
                <div>
                  <DialogTitle className="font-display text-base">
                    Vanish Request Sent
                  </DialogTitle>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {sentCount} sent · {errorCount} failed
                  </p>
                </div>
              </div>
              <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
                Your kind 62 event was broadcast. Compliant relays will delete all your events and
                block re-broadcasting. Non-compliant relays will ignore the request.
              </DialogDescription>
            </DialogHeader>

            {/* Per-relay results */}
            <div className="max-h-56 overflow-auto space-y-1.5 pr-1">
              {results.map((r) => (
                <div
                  key={r.relay}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg border text-xs',
                    r.status === 'sent'
                      ? 'border-accent/20 bg-accent/5'
                      : 'border-border bg-secondary/20'
                  )}
                >
                  {r.status === 'sent' ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  )}
                  <code className="font-mono text-muted-foreground flex-1 truncate">
                    {r.relay.replace(/^wss?:\/\//, '')}
                  </code>
                  <span className={cn(
                    'flex-shrink-0 font-medium',
                    r.status === 'sent' ? 'text-accent' : 'text-muted-foreground'
                  )}>
                    {r.status === 'sent' ? 'sent' : r.error ?? 'error'}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-[11px] text-muted-foreground leading-relaxed">
              "Sent" means the event was accepted by the relay's WebSocket. Actual deletion
              depends on whether the relay supports NIP-62. Most current relays are not yet
              compliant — this feature is forward-looking.
            </p>

            <DialogFooter>
              <Button onClick={() => handleClose(false)} variant="outline">
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
